import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { DEPRECATED_PREFIXES } from '@/hooks/useLocalStorageManager';
import { isPreviewAuthEnvironment } from '@/utils/authEnvironment';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isWhitelisted: boolean | null;
  whitelistLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  enableDemoMode: () => void;
  enablePreviewMode: () => Promise<void>;
  isDemoMode: boolean;
  isPreviewMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function for auth error messages
const getAuthErrorMessage = (error: any): string => {
  if (error.message?.includes('redirect')) {
    return "Cấu hình redirect URL không đúng. Vui lòng kiểm tra cài đặt Google OAuth.";
  } else if (error.message?.includes('consent')) {
    return "Google OAuth Consent Screen chưa được cấu hình đúng. Vui lòng kiểm tra cài đặt.";
  } else if (error.message?.includes('domain')) {
    return "Domain chưa được authorize trong Google OAuth. Vui lòng thêm domain vào Authorized domains.";
  }
  return `Không thể kết nối với Google: ${error.message}`;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState<boolean | null>(null);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const { toast } = useToast();
  
  // Refs to track sign-in toast - prevents showing on token refresh/navigation
  const hasShownSignInToastRef = useRef(false);
  const lastSignedInUserIdRef = useRef<string | null>(null);
  
  // Ref to track popup check interval for proper cleanup
  const popupCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Ref to track when tab was hidden (for throttled session expiry detection)
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    // Skip auth setup only in demo mode
    // Preview mode needs auth listeners to sync session state on tab resume
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    logger.debug('🔧 Setting up auth listeners...');
    
    // Faster timeout for preview environments - reduced timeout for better UX
    const timeoutDuration = isPreviewMode ? 2000 : 4000; // 2s for preview, 4s for normal
    const authTimeout = setTimeout(() => {
      logger.warn('⚠️ Auth initialization timeout - clearing loading state');
      setLoading(false);
      toast({
        title: "Timeout xác thực",
        description: "Không thể kết nối với dịch vụ xác thực. Vui lòng thử lại.",
        variant: "destructive"
      });
    }, timeoutDuration);

    let retryCount = 0;
    const maxRetries = 1;
    const retryDelay = 800;

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, session) => {
            logger.debug('🔄 Auth state change:', { 
              event, 
              hasSession: !!session, 
              hasAccessToken: !!session?.access_token,
              userId: session?.user?.id,
              provider: session?.user?.app_metadata?.provider
            });
            
            setSession(session);
            setUser(session?.user ?? null);
            
            // Clear timeout khi có auth state change
            clearTimeout(authTimeout);
            
            // Always clear loading state on any auth event
            setLoading(false);

            if (event === 'SIGNED_IN') {
              const currentUserId = session?.user?.id;
              
              logger.debug('✅ User signed in successfully:', {
                provider: session?.user?.app_metadata?.provider,
                email: session?.user?.email,
                userId: currentUserId
              });
              
              // Check whitelist asynchronously để không block UI
              setTimeout(() => {
                checkWhitelist(session?.user?.email || '');
              }, 0);
              
              // Only show toast for actual new sign-in (not token refresh/navigation)
              const isNewSignIn = !hasShownSignInToastRef.current || 
                                  (currentUserId && lastSignedInUserIdRef.current !== currentUserId);
              
              if (isNewSignIn && session?.user) {
                hasShownSignInToastRef.current = true;
                lastSignedInUserIdRef.current = currentUserId || null;
                
                toast({
                  title: "Đăng nhập thành công",
                  description: "Chào mừng bạn quay trở lại!"
                });
              }
            }

            if (event === 'SIGNED_OUT') {
              logger.debug('👋 User signed out');
              setIsWhitelisted(null);
              setWhitelistLoading(false);
              
              // Reset sign-in toast tracking on sign out
              hasShownSignInToastRef.current = false;
              lastSignedInUserIdRef.current = null;
              
              // Clear any problematic localStorage data on sign out using standardized patterns
              try {
                localStorage.removeItem('app-whitelist-cache');
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key && DEPRECATED_PREFIXES.some(prefix => key.startsWith(prefix))) {
                    keysToRemove.push(key);
                  }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                logger.debug('🧹 Cleared deprecated localStorage keys on sign out using standardized cleanup');
              } catch (error) {
                logger.error('Error clearing localStorage:', error);
              }
            }

            if (event === 'TOKEN_REFRESHED') {
              logger.debug('🔄 Token refreshed successfully');
            }

            if (event === 'USER_UPDATED') {
              logger.debug('👤 User profile updated');
            }
          }
        );

        // THEN check for existing session with retry logic
        const checkSession = async (attempt = 0) => {
          try {
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (error) {
              throw error;
            }

            logger.debug('🔍 Initial session check:', { hasSession: !!session, hasAccessToken: !!session?.access_token, attempt });
            
            setSession(session);
            setUser(session?.user ?? null);
            
            // Clear timeout on successful session check
            clearTimeout(authTimeout);
            setLoading(false);
          } catch (error) {
            logger.error(`Session check failed (attempt ${attempt + 1}):`, error);
            
            if (attempt < maxRetries) {
              setTimeout(() => checkSession(attempt + 1), retryDelay * (attempt + 1));
            } else {
              // After max retries, try to recover from cached session before failing
              try {
                const cachedSession = sessionStorage.getItem('preview_auth_session');
                if (cachedSession) {
                  const { session: recoveredSession, timestamp } = JSON.parse(cachedSession);
                  if (Date.now() - timestamp < 300000) { // 5 minutes
                    logger.debug('🔄 Recovering from cached session');
                    setSession(recoveredSession);
                    setUser(recoveredSession?.user ?? null);
                    setLoading(false);
                    clearTimeout(authTimeout);
                    return;
                  }
                }
              } catch (e) {
                logger.warn('Failed to recover from cache:', e);
              }
              
              // After max retries and cache recovery, set loading to false and clear session
              clearTimeout(authTimeout);
              setSession(null);
              setUser(null);
              setLoading(false);
              toast({
                title: "Lỗi xác thực",
                description: "Không thể khôi phục phiên đăng nhập. Vui lòng thử lại.",
                variant: "destructive"
              });
            }
          }
        };

        checkSession();
        
        return () => {
          clearTimeout(authTimeout);
          subscription.unsubscribe();
        };
      } catch (error) {
        logger.error('Auth initialization error:', error);
        clearTimeout(authTimeout);
        setLoading(false);
        toast({
          title: "Lỗi khởi tạo",
          description: "Có lỗi xảy ra khi khởi tạo xác thực. Vui lòng thử lại.",
          variant: "destructive"
        });
      }
    };

    const cleanup = initializeAuth();
    return () => {
      clearTimeout(authTimeout);
      // Cleanup popup check interval if it exists
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
      if (cleanup instanceof Promise) {
        cleanup.then(fn => fn && fn());
      }
    };
  }, [toast, isDemoMode, isPreviewMode]);

  // Global message bridge: commit Supabase session sent from popup/iframe
  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      try {
        if (
          event.data?.type === 'SUPABASE_AUTH_TOKEN' &&
          event.data.session?.access_token &&
          event.data.session?.refresh_token
        ) {
          logger.debug('🔐 Committing session from message bridge');
          const { error } = await supabase.auth.setSession({
            access_token: event.data.session.access_token,
            refresh_token: event.data.session.refresh_token,
          });
          if (error) throw error;
        }
      } catch (e) {
        logger.error('❌ Failed to commit session from message bridge:', e);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Optimized whitelist check with circuit breaker + localStorage cache
  const WHITELIST_CACHE_KEY = 'app-whitelist-cache';
  const WHITELIST_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  const checkWhitelist = async (email: string) => {
    if (!email) return;
    
    // Try to use cached value to avoid spinner
    let hasCachedValue = false;
    try {
      const raw = localStorage.getItem(WHITELIST_CACHE_KEY);
      if (raw) {
        const { email: cachedEmail, isWhitelisted: cachedResult, timestamp } = JSON.parse(raw);
        if (cachedEmail === email && Date.now() - timestamp < WHITELIST_CACHE_TTL) {
          setIsWhitelisted(cachedResult); // instant — no spinner
          hasCachedValue = true;
          logger.debug('⚡ Whitelist cache hit, skipping spinner');
        }
      }
    } catch { /* ignore parse errors */ }
    
    // Only show loading spinner on first-ever check (no cached value)
    if (!hasCachedValue) {
      setWhitelistLoading(true);
    }
    
    const controller = new AbortController();
    const timeoutDuration = isPreviewMode ? 3000 : 6000; // Faster for preview
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
    
    try {
      // Single combined whitelist check RPC for better performance
      const { data, error } = await supabase.rpc('combined_whitelist_check', 
        { user_email: email }
      );
      
      clearTimeout(timeoutId);
      
      if (error) throw error;
      
      const { regular_whitelisted = false, enhanced_whitelisted = false } = data?.[0] || {};
      
      // User must pass enhanced whitelist
      if (!enhanced_whitelisted) {
        logger.warn('🚫 Enhanced whitelist failed for:', email);
        setIsWhitelisted(false);
        setWhitelistLoading(false);
        
        // Clear cache on denial
        try { localStorage.removeItem(WHITELIST_CACHE_KEY); } catch {}
        
        // Sign out and redirect
        await supabase.auth.signOut();
        window.location.replace('/auth?denied=1');
        toast({
          title: "Truy cập bị từ chối",
          description: "Tài khoản của bạn chưa được cấp quyền truy cập.",
          variant: "destructive",
        });
        return;
      }
      
      setIsWhitelisted(regular_whitelisted);
      
      // Write cache for future use (tab resume, refresh, new tabs)
      try {
        localStorage.setItem(WHITELIST_CACHE_KEY, JSON.stringify({
          email, isWhitelisted: regular_whitelisted, timestamp: Date.now()
        }));
      } catch { /* ignore quota errors */ }
      
      logger.debug('✅ Combined whitelist check complete:', { regular: regular_whitelisted, enhanced: enhanced_whitelisted, cached: hasCachedValue });
    } catch (error) {
      clearTimeout(timeoutId);
      logger.error('❌ Whitelist check error:', error);
      // Only override if we don't have a cached value
      if (!hasCachedValue) {
        setIsWhitelisted(false);
      }
    } finally {
      setWhitelistLoading(false);
    }
  };

  // Check whitelist when user changes (skip in demo mode and preview mode)
  useEffect(() => {
    if (isDemoMode || isPreviewMode) {
      setIsWhitelisted(true);
      setWhitelistLoading(false);
      return;
    }

    if (user?.email && !loading) {
      checkWhitelist(user.email);
    } else if (!user) {
      setIsWhitelisted(null);
      setWhitelistLoading(false);
    }
  }, [user?.email, loading, isDemoMode, isPreviewMode]);

  // Detect expired session when user returns to tab after extended absence
  useEffect(() => {
    if (isDemoMode || isPreviewMode) return;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }
      // Throttle: only check if hidden > 2 minutes
      if (!hiddenAtRef.current || Date.now() - hiddenAtRef.current < 2 * 60 * 1000) return;
      hiddenAtRef.current = null;

      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession && user) {
          logger.warn('⚠️ Session expired while tab was hidden');
          toast({ title: "Phiên đăng nhập hết hạn", description: "Vui lòng đăng nhập lại.", variant: "destructive" });
          setUser(null);
          setSession(null);
          setIsWhitelisted(null);
          try { localStorage.removeItem('app-whitelist-cache'); } catch {}
        }
      } catch (error) {
        logger.error('❌ Visibility change session check error:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, isDemoMode, isPreviewMode, toast]);

  const signInWithGoogle = async () => {
    try {
      // Centralized preview environment detection
      const isPreview = isPreviewAuthEnvironment();
      const redirectUrl = `${window.location.origin}/auth`;
      
      logger.debug('🚀 Initiating Google OAuth:', { 
        redirectUrl, 
        isPreview,
        hostname: window.location.hostname
      });
      
      // For preview environments, use popup authentication
      if (isPreview) {
        logger.debug('🪟 Using popup authentication for preview environment');
        
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectUrl,
            scopes: 'openid profile email',
            queryParams: {
              access_type: 'offline',
              prompt: 'consent'
            },
            skipBrowserRedirect: true
          }
        });

        if (error) {
          logger.error('❌ Google OAuth error in popup:', error);
          toast({
            title: "Lỗi đăng nhập",
            description: `Không thể kết nối với Google: ${error.message}`,
            variant: "destructive"
          });
          return;
        }

        if (data?.url) {
          logger.debug('🚀 Opening OAuth URL in popup:', data.url);
          
          // Create popup window with optimal size
          const popup = window.open(
            data.url, 
            'google-auth-popup',
            'width=500,height=600,scrollbars=yes,resizable=yes,centerscreen=yes'
          );

          if (!popup) {
            // Fallback to new tab if popup is blocked
            logger.warn('🚫 Popup blocked, falling back to new tab');
            window.open(data.url, '_blank', 'noopener,noreferrer');
            toast({
              title: "Popup bị chặn",
              description: "Đã mở trong tab mới. Hoàn tất đăng nhập và quay lại đây.",
            });
            return;
          }

          toast({
            title: "Đang đăng nhập",
            description: "Vui lòng hoàn tất đăng nhập trong popup.",
          });

          // Listen for auth state changes to close popup and show success
          const authSubscription = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session && !popup.closed) {
              logger.debug('✅ Authentication successful in popup, closing...');
              popup.close();
              toast({
                title: "Đăng nhập thành công",
                description: `Chào mừng ${session.user.email || 'bạn'}!`,
              });
              // Clean up subscription
              authSubscription.data.subscription.unsubscribe();
            }
          });

          // Monitor popup status and clean up if closed manually
          // Clear any existing interval first
          if (popupCheckIntervalRef.current) {
            clearInterval(popupCheckIntervalRef.current);
          }
          
          popupCheckIntervalRef.current = setInterval(() => {
            if (popup.closed) {
              if (popupCheckIntervalRef.current) {
                clearInterval(popupCheckIntervalRef.current);
                popupCheckIntervalRef.current = null;
              }
              authSubscription.data.subscription.unsubscribe();
              logger.debug('🪟 Popup closed by user before completion');
            }
          }, 1000);

          return;
        }
      }
      
      // Normal OAuth flow for non-iframe environments
      logger.debug('🔐 Using normal OAuth flow (no iframe)');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          scopes: 'openid profile email',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      logger.debug('🔍 OAuth response:', { 
        hasData: !!data, 
        hasUrl: !!data?.url,
        hasError: !!error,
        errorCode: error?.message
      });

      if (error) {
        logger.error('❌ Google OAuth error:', {
          message: error.message,
          status: error.status,
          details: error
        });
        
        // Enhanced error messages based on common issues
        let userMessage = `Không thể kết nối với Google: ${error.message}`;
        if (error.message?.includes('redirect')) {
          userMessage = "Cấu hình redirect URL không đúng. Vui lòng kiểm tra cài đặt Google OAuth.";
        } else if (error.message?.includes('consent')) {
          userMessage = "Google OAuth Consent Screen chưa được cấu hình đúng. Vui lòng kiểm tra cài đặt.";
        } else if (error.message?.includes('domain')) {
          userMessage = "Domain chưa được authorize trong Google OAuth. Vui lòng thêm domain vào Authorized domains.";
        }
        
        toast({
          title: "Lỗi đăng nhập",
          description: userMessage,
          variant: "destructive"
        });
        return;
      }

      // Success case will be handled by the auth state change listener above
      if (data?.url) {
        logger.debug('🚀 Redirecting to OAuth URL');
      }
    } catch (error) {
      logger.error('❌ Unexpected error during Google sign in:', error);
      
      toast({
        title: "Lỗi đăng nhập",
        description: getAuthErrorMessage(error),
        variant: "destructive"
      });
    }
  };

  const signOut = async () => {
    try {
      // Clear all auth state first
      setUser(null);
      setSession(null);
      setIsWhitelisted(null);
      setWhitelistLoading(false);
      setIsDemoMode(false);
      setIsPreviewMode(false);
      
      // Clear caches
      sessionStorage.removeItem('preview_auth_session');
      localStorage.removeItem('app-whitelist-cache');
      
      // Sign out from Supabase (this will trigger the SIGNED_OUT event)
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error('❌ Sign out error:', error);
        // Don't throw here, as we've already cleared state above
        toast({
          title: "Cảnh báo",
          description: "Có lỗi khi đăng xuất, nhưng bạn đã được đăng xuất khỏi ứng dụng.",
          variant: "destructive"
        });
      } else {
        logger.debug('✅ Successfully signed out');
        toast({
          title: "Đã đăng xuất",
          description: "Bạn đã đăng xuất thành công."
        });
      }
    } catch (error) {
      logger.error('❌ Unexpected sign out error:', error);
      toast({
        title: "Lỗi đăng xuất",
        description: "Có lỗi xảy ra khi đăng xuất.",
        variant: "destructive"
      });
    }
  };

  const enableDemoMode = () => {
    setIsDemoMode(true);
    setIsPreviewMode(false);
    setLoading(false);
    setWhitelistLoading(false);
    
    // Create mock user and session for demo mode
    const mockUser = {
      id: 'demo-user-123',
      email: 'demo@example.com',
      user_metadata: { full_name: 'Demo User' },
      app_metadata: { provider: 'demo' },
      aud: 'authenticated',
      created_at: new Date().toISOString(),
      role: 'authenticated'
    };

    const mockSession = {
      access_token: 'demo-token-123',
      refresh_token: 'demo-refresh-123',
      expires_in: 3600,
      expires_at: Date.now() / 1000 + 3600,
      token_type: 'bearer',
      user: mockUser
    };

    setUser(mockUser as any);
    setSession(mockSession as any);
    setIsWhitelisted(true);
    setLoading(false);
    setWhitelistLoading(false);

    toast({
      title: "Demo Mode",
      description: "Đang sử dụng dữ liệu demo cho preview",
    });
  };

  // Helper function to fetch real preview session from Edge Function
  const fetchPreviewSession = async (): Promise<{
    access_token: string;
    refresh_token: string;
    expires_at: number;
  } | null> => {
    try {
      const response = await fetch(
        'https://dyakqbhcepjcommcflru.supabase.co/functions/v1/preview-auth',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR5YWtxYmhjZXBqY29tbWNmbHJ1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYwNDMwMTcsImV4cCI6MjA3MTYxOTAxN30.oSKe3VAoqj-NZ19it8WfRB01_4MaFZupNdGzHRqDgCM',
            // ✅ Security: Send preview secret for verification
            'X-Preview-Secret': import.meta.env.VITE_PREVIEW_AUTH_SECRET || '',
          },
        }
      );

      if (!response.ok) {
        logger.error('[Preview] Failed to fetch preview session:', response.status);
        return null;
      }
      
      const data = await response.json();
      return data.success ? data.session : null;
    } catch (error) {
      logger.error('[Preview] Error fetching preview session:', error);
      return null;
    }
  };

  const enablePreviewMode = async () => {
    return new Promise<void>(async (resolve, reject) => {
      // Setup timeout for preview mode - increased to 10s for Edge Function call
      const timeout = setTimeout(() => {
        logger.error('❌ Preview mode setup timeout');
        setIsPreviewMode(false);
        setLoading(false);
        reject(new Error('Preview mode setup timeout'));
      }, 10000);

      try {
        logger.debug('🎬 Enabling preview mode with real JWT...');
        setIsPreviewMode(true);
        setLoading(true);

        // Fetch real JWT from Edge Function
        const previewSession = await fetchPreviewSession();
        
        if (!previewSession) {
          throw new Error('Failed to get preview session from Edge Function');
        }

        // Set real session into Supabase client
        const { error: setSessionError } = await supabase.auth.setSession({
          access_token: previewSession.access_token,
          refresh_token: previewSession.refresh_token,
        });

        if (setSessionError) {
          throw setSessionError;
        }

        // Get user from real session
        const { data: { user: realUser } } = await supabase.auth.getUser();
        
        setSession({
          access_token: previewSession.access_token,
          refresh_token: previewSession.refresh_token,
          expires_at: previewSession.expires_at,
          expires_in: 3600,
          token_type: 'bearer',
          user: realUser,
        } as any);
        setUser(realUser);
        setIsWhitelisted(true);
        setWhitelistLoading(false);
        setLoading(false);
        
        clearTimeout(timeout);
        logger.debug('✅ Preview mode enabled with real JWT');
        resolve();
        
      } catch (error) {
        clearTimeout(timeout);
        logger.error('❌ Failed to enable preview mode:', error);
        setIsPreviewMode(false);
        setLoading(false);
        reject(error);
      }
    });
  };

  const value = {
    user,
    session,
    loading,
    isWhitelisted,
    whitelistLoading,
    signInWithGoogle,
    signOut,
    enableDemoMode,
    enablePreviewMode,
    isDemoMode,
    isPreviewMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};